import { displayShopFact } from '@/i18n/catalogDisplayText';
import { fixedUi } from '@/i18n/fixedUi';
import React, { useMemo, useState } from 'react';
import { buildDeploymentRegionGroups } from '@/temu-platform/utils/temuDeploymentRegions';
import { countSiteRows } from '@/temu-platform/utils/temuSiteList';
import {
  TEMU_MODE_SEMI,
  resolveTemuShopMode,
} from '@/temu-platform/utils/temuShopCapabilities';

const PREVIEW_COUNT = 3;

function RegionSitesCell({ sites }) {
  const [expanded, setExpanded] = useState(false);
  const total = sites.length;
  const needsMore = total > PREVIEW_COUNT;
  const visible = expanded || !needsMore ? sites : sites.slice(0, PREVIEW_COUNT);
  const names = visible.map((s) => displayShopFact(s.siteName)).join(', ');

  return (
    <div className="tpo-shop-info__sites-cell">
      <span className="tpo-shop-info__sites-text">{names}</span>
      {needsMore ? (
        <button
          type="button"
          className="tpo-shop-info__sites-more"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? fixedUi('收起') : `${fixedUi('更多')}(${total})`}
        </button>
      ) : null}
    </div>
  );
}

function AllSitesTable() {
  return (
    <div className="tpo-shop-info__table-wrap">
      <table className="tpo-shop-info__table">
        <thead>
          <tr>
            <th>{fixedUi("主站点")}</th>
            <th>{fixedUi("子站点")}</th>
            <th>{fixedUi("站点缩写")}</th>
            <th>{fixedUi("状态")}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>TEMU</td>
            <td>{fixedUi("全站点")}</td>
            <td><code>ALL</code></td>
            <td>{fixedUi("启用")}</td>
          </tr>
        </tbody>
      </table>
      <p className="tpo-shop-info__panel-hint" style={{ marginTop: 8 }}>
        {fixedUi("全托管店铺售卖站点默认按全站点（ALL）处理；分区站点仅半托管店铺展示。")}
      </p>
    </div>
  );
}

/**
 * 店铺信息 · 售卖站点。
 * 半托管：按美区 / 欧区 / 全球展示，更多展开完整站点。
 * 全托管等其它类型：固定全站点 ALL。
 */
export default function ShopSiteListPanel({
  loading,
  error,
  siteList,
  shopType = '',
}) {
  const mode = useMemo(() => resolveTemuShopMode(shopType), [shopType]);
  const showByRegion = mode === TEMU_MODE_SEMI;
  const groups = useMemo(
    () => (showByRegion ? buildDeploymentRegionGroups(siteList) : []),
    [showByRegion, siteList],
  );
  const shopSiteCount = useMemo(
    () => (showByRegion ? countSiteRows(siteList) : 0),
    [showByRegion, siteList],
  );

  return (
    <div className="tpo-shop-info__panel" data-testid="temu-site-list">
      {loading ? <p className="tpo-return-detail__empty">{fixedUi("正在加载站点信息…")}</p> : null}
      {error ? <p className="tpo-shop-info__inline-error" role="alert">{error}</p> : null}

      {!loading && !error && !showByRegion ? <AllSitesTable /> : null}

      {!loading && !error && showByRegion && groups.length === 0 ? (
        <p className="tpo-return-detail__empty">{fixedUi("暂无站点数据。")}</p>
      ) : null}

      {!loading && !error && showByRegion && groups.length > 0 ? (
        <div className="tpo-shop-info__table-wrap">
          <table className="tpo-shop-info__table">
            <thead>
              <tr>
                <th>{fixedUi("部署区")}</th>
                <th>{fixedUi("站点")}</th>
                <th>{fixedUi("数量")}</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <tr key={group.key}>
                  <td>{displayShopFact(group.label)}</td>
                  <td>
                    <RegionSitesCell sites={group.sites} />
                  </td>
                  <td>{group.sites.length}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="tpo-shop-info__panel-hint" style={{ marginTop: 8 }}>
            {shopSiteCount === 0
              ? fixedUi('半托管未同步到本店站点时按官方已开站目录分区展示；仓库同步拿到站点后会替换为本店列表。全托管店铺固定展示全站点。')
              : fixedUi('售卖站点按美区 / 欧区 / 全球分区展示（半托管）；全托管店铺固定展示全站点。')}
          </p>
        </div>
      ) : null}
    </div>
  );
}
